<template>
  <section class="products-section">
    <div class="section-container">
      <h2 class="section-title">核心产品</h2>
      <p class="section-desc">
        按产品线浏览专业级地质工程软件，使用左右按钮查看同一系列下的全部产品。
      </p>

      <template v-if="loading">
        <div class="series-row skeleton-row" v-for="i in 2" :key="'skeleton-row-' + i">
          <div class="series-row-header">
            <div class="skeleton-title"></div>
            <div class="skeleton-subtitle"></div>
          </div>
          <div class="series-grid">
            <div class="product-card skeleton" v-for="j in 3" :key="'skeleton-' + i + '-' + j">
              <div class="skeleton-icon"></div>
              <div class="skeleton-title"></div>
              <div class="skeleton-subtitle"></div>
              <div class="skeleton-text"></div>
              <div class="skeleton-text short"></div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div
          v-for="series in seriesList"
          :id="`series-${series.slug}`"
          :key="series.slug"
          class="series-row"
          :style="{ '--visible-cards': visibleCount }"
        >
          <div class="series-row-header">
            <div>
              <h3 class="series-name">{{ series.name }}</h3>
              <p v-if="series.tagline" class="series-tagline">{{ series.tagline }}</p>
            </div>
          </div>

          <!-- ≤3：等高网格，无按钮 -->
          <div
            v-if="!showNav(series)"
            class="series-grid"
            :aria-label="`${series.name} 产品列表`"
          >
            <article
              v-for="product in series.products"
              :key="product.id"
              class="product-card"
            >
              <div class="card-glow"></div>
              <div class="card-content">
                <div class="icon-wrapper">
                  <ProductImage
                    :src="product.image_url || product.image_data || ''"
                    :name="product.name"
                    class="product-image"
                  />
                </div>
                <h3 class="product-name">{{ product.name }}</h3>
                <div class="product-subtitle">{{ product.name_sub }}</div>
                <p class="product-desc">{{ product.description }}</p>
                <div class="product-features">
                  <span
                    v-for="(feature, index) in product.features"
                    :key="index"
                    class="feature-tag"
                  >
                    {{ feature }}
                  </span>
                </div>
                <a :href="product.url" class="learn-more">
                  了解详情
                  <span class="arrow">→</span>
                </a>
              </div>
            </article>
          </div>

          <!-- >3：左右按钮分页 -->
          <div v-else class="series-carousel">
            <button
              v-if="canGoPrev(series)"
              type="button"
              class="nav-btn nav-prev"
              :aria-label="`查看上一组 ${series.name} 产品`"
              @click="goPrev(series.slug)"
            >
              ‹
            </button>
            <div class="series-viewport">
              <div
                class="series-track"
                :style="trackStyle(series)"
                :aria-label="`${series.name} 产品列表`"
              >
                <article
                  v-for="(product, productIndex) in series.products"
                  :key="product.id"
                  class="product-card product-card--carousel"
                  :class="{ 'product-card--offscreen': !isCardInView(series, productIndex) }"
                  :aria-hidden="isCardInView(series, productIndex) ? undefined : 'true'"
                >
                  <div class="card-glow"></div>
                  <div class="card-content">
                    <div class="icon-wrapper">
                      <ProductImage
                        :src="product.image_url || product.image_data || ''"
                        :name="product.name"
                        class="product-image"
                      />
                    </div>
                    <h3 class="product-name">{{ product.name }}</h3>
                    <div class="product-subtitle">{{ product.name_sub }}</div>
                    <p class="product-desc">{{ product.description }}</p>
                    <div class="product-features">
                      <span
                        v-for="(feature, index) in product.features"
                        :key="index"
                        class="feature-tag"
                      >
                        {{ feature }}
                      </span>
                    </div>
                    <a :href="product.url" class="learn-more">
                      了解详情
                      <span class="arrow">→</span>
                    </a>
                  </div>
                </article>
              </div>
            </div>
            <button
              v-if="canGoNext(series)"
              type="button"
              class="nav-btn nav-next"
              :aria-label="`查看下一组 ${series.name} 产品`"
              @click="goNext(series.slug, series.products.length)"
            >
              ›
            </button>
          </div>
        </div>

        <div class="api-note" v-if="seriesList.length === 0">
          <div class="note-box">
            <span class="status-dot"></span>
            暂无已发布的产品系列，请在管理后台维护后刷新。
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import ProductImage from './ProductImage.vue';

const MOBILE_BREAKPOINT = 768;
const DESKTOP_VISIBLE = 3;
const MOBILE_VISIBLE = 1;
const CARD_GAP_REM = 2;

export default {
  name: 'ProductsSection',
  components: { ProductImage },
  data() {
    return {
      seriesList: [],
      loading: true,
      scrollIndexBySlug: {},
      /** 滑动动画期间临时扩大可见范围，避免离场卡瞬间 visibility:hidden */
      animRangeBySlug: {},
      animTimerBySlug: {},
      visibleCount: DESKTOP_VISIBLE,
    };
  },
  mounted() {
    this.updateVisibleCount();
    window.addEventListener('resize', this.handleResize);
    this.fetchSeriesData();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    Object.keys(this.animTimerBySlug).forEach((slug) => {
      clearTimeout(this.animTimerBySlug[slug]);
    });
  },
  methods: {
    async fetchSeriesData() {
      try {
        const response = await axios.get('/api/product-series/');
        this.seriesList = Array.isArray(response.data) ? response.data : [];
        const nextIndex = {};
        this.seriesList.forEach((series) => {
          nextIndex[series.slug] = 0;
        });
        this.scrollIndexBySlug = nextIndex;
        this.animRangeBySlug = {};
      } catch (error) {
        console.error('Error fetching product series:', error);
        this.seriesList = [];
        this.scrollIndexBySlug = {};
        this.animRangeBySlug = {};
      } finally {
        this.loading = false;
      }
    },
    updateVisibleCount() {
      this.visibleCount = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
        ? MOBILE_VISIBLE
        : DESKTOP_VISIBLE;
    },
    handleResize() {
      this.updateVisibleCount();
      const nextIndex = { ...this.scrollIndexBySlug };
      this.seriesList.forEach((series) => {
        const maxStart = Math.max(0, series.products.length - this.visibleCount);
        const current = nextIndex[series.slug] || 0;
        nextIndex[series.slug] = Math.min(current, maxStart);
      });
      this.scrollIndexBySlug = nextIndex;
      this.animRangeBySlug = {};
    },
    showNav(series) {
      return (series.products || []).length > 3;
    },
    scrollIndex(slug) {
      return this.scrollIndexBySlug[slug] || 0;
    },
    canGoPrev(series) {
      return this.scrollIndex(series.slug) > 0;
    },
    canGoNext(series) {
      const index = this.scrollIndex(series.slug);
      return index + this.visibleCount < (series.products || []).length;
    },
    /**
     * 当前应绘制的卡片：静止时仅视口内；滑动时保留进出场卡直至动画结束。
     */
    isCardInView(series, productIndex) {
      const range = this.animRangeBySlug[series.slug];
      if (range) {
        return productIndex >= range.start && productIndex < range.end;
      }
      const start = this.scrollIndex(series.slug);
      return productIndex >= start && productIndex < start + this.visibleCount;
    },
    clearAnimRange(slug) {
      if (this.animTimerBySlug[slug]) {
        clearTimeout(this.animTimerBySlug[slug]);
        const timers = { ...this.animTimerBySlug };
        delete timers[slug];
        this.animTimerBySlug = timers;
      }
      if (!this.animRangeBySlug[slug]) return;
      const ranges = { ...this.animRangeBySlug };
      delete ranges[slug];
      this.animRangeBySlug = ranges;
    },
    measureStepPx(slug) {
      const row = this.$el && this.$el.querySelector(`#series-${slug}`);
      if (!row) return 0;
      const track = row.querySelector('.series-track');
      const card = row.querySelector('.product-card--carousel:not(.product-card--offscreen)')
        || row.querySelector('.product-card--carousel');
      if (!track || !card) return 0;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return card.getBoundingClientRect().width + gap;
    },
    cssTransform(slug, index) {
      if (index <= 0) return 'translate3d(0px, 0px, 0px)';
      const step = this.measureStepPx(slug);
      if (!step) {
        const stepPercent = 100 / this.visibleCount;
        const gapAdjust = CARD_GAP_REM / this.visibleCount;
        return `translate3d(calc(-${index * stepPercent}% - ${index * gapAdjust}rem), 0px, 0px)`;
      }
      return `translate3d(${-(index * step)}px, 0px, 0px)`;
    },
    /**
     * 翻页：用 Web Animations 做位移；动画结束前不改 scrollIndex，
     * 避免向右时插入「上一页」按钮触发 Vue 重渲染打断过渡。
     */
    animateToIndex(slug, fromIndex, toIndex) {
      if (this.animRangeBySlug[slug]) return;
      const start = Math.min(fromIndex, toIndex);
      const end = Math.max(fromIndex, toIndex) + this.visibleCount;
      this.animRangeBySlug = {
        ...this.animRangeBySlug,
        [slug]: { start, end },
      };
      this.$nextTick(() => {
        const row = this.$el.querySelector(`#series-${slug}`);
        const track = row && row.querySelector('.series-track');
        if (!track || typeof track.animate !== 'function') {
          this.scrollIndexBySlug = { ...this.scrollIndexBySlug, [slug]: toIndex };
          this.clearAnimRange(slug);
          return;
        }
        const fromTx = this.cssTransform(slug, fromIndex);
        const toTx = this.cssTransform(slug, toIndex);
        track.style.transition = 'none';
        track.style.transform = fromTx;
        void track.offsetWidth;
        const anim = track.animate(
          [{ transform: fromTx }, { transform: toTx }],
          { duration: 350, easing: 'ease', fill: 'forwards' },
        );
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          try {
            if (typeof anim.commitStyles === 'function') anim.commitStyles();
          } catch (e) { /* ignore */ }
          try { anim.cancel(); } catch (e) { /* ignore */ }
          track.style.transition = 'none';
          track.style.transform = toTx;
          this.scrollIndexBySlug = {
            ...this.scrollIndexBySlug,
            [slug]: toIndex,
          };
          this.clearAnimRange(slug);
          this.$nextTick(() => {
            track.style.transition = '';
            // 保持与 Vue :style 一致的终点值，避免清空后出现 transform:none
            track.style.transform = this.cssTransform(slug, toIndex);
          });
        };
        anim.onfinish = finish;
        if (this.animTimerBySlug[slug]) clearTimeout(this.animTimerBySlug[slug]);
        this.animTimerBySlug = {
          ...this.animTimerBySlug,
          [slug]: setTimeout(finish, 400),
        };
      });
    },
    goPrev(slug) {
      if (this.animRangeBySlug[slug]) return;
      const current = this.scrollIndex(slug);
      if (current <= 0) return;
      this.animateToIndex(slug, current, current - 1);
    },
    goNext(slug, productCount) {
      if (this.animRangeBySlug[slug]) return;
      const current = this.scrollIndex(slug);
      const maxStart = Math.max(0, productCount - this.visibleCount);
      if (current >= maxStart) return;
      this.animateToIndex(slug, current, current + 1);
    },
    trackStyle(series) {
      return {
        transform: this.cssTransform(series.slug, this.scrollIndex(series.slug)),
      };
    },
  },
};
</script>

<style scoped>
.products-section {
  background-color: var(--bg-color-secondary);
  border-top: 1px solid rgba(15, 23, 42, 0.05);
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
  scroll-margin-top: calc(var(--nav-height, 80px) + 1rem);
  overflow: visible;
}

.products-section :deep(.section-container),
.products-section .section-container {
  overflow: visible;
}

.section-desc {
  text-align: center;
  color: var(--text-secondary);
  max-width: 600px;
  margin: -1.5rem auto 4rem;
  font-size: 1.1rem;
}

.series-row {
  /* 与网格/标题共用，保证对齐且不额外收窄内容列 */
  --content-inline: 1rem;
  max-width: 1440px;
  margin: 0 auto 2.75rem;
  padding: 0;
  /* 允许箭头伸入页面外侧边距，不占用卡片宽度 */
  overflow: visible;
}

.series-row:last-of-type {
  margin-bottom: 1rem;
}

.series-row-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 0 var(--content-inline);
}

.series-name {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.45rem;
  font-weight: 700;
}

.series-tagline {
  margin: 0.35rem 0 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.series-grid {
  display: grid;
  /* 与横滑 visibleCount 共用 --visible-cards，避免 1024 两列 vs 横滑三列导致卡宽不一致 */
  grid-template-columns: repeat(var(--visible-cards, 3), minmax(0, 1fr));
  gap: 2rem;
  align-items: stretch;
  padding: 16px var(--content-inline) 40px;
}

.series-carousel {
  /* 按钮槽本身不铺底、不裁切，阴影可穿过 */
  position: relative;
  padding: 0 var(--content-inline);
  overflow: visible;
  background: transparent;
}

.series-viewport {
  /*
   * overflow:hidden 在「内容盒外扩 20px」处裁切：
   * - 离场卡滑出后不会停在 gutter 再被 visibility:hidden 闪掉；
   * - 左右 20px 出血保留首尾卡阴影/圆角，避免 clip-path 贴边裁切造成遮挡感；
   * - 负 margin + 加宽抵消出血，卡片列宽与标题对齐；
   * - 上下留白容纳 hover translateY(-8px) 与底影。
   */
  --shadow-bleed: 20px;
  overflow: hidden;
  width: calc(100% + 2 * var(--shadow-bleed));
  margin: 0 calc(-1 * var(--shadow-bleed));
  box-sizing: border-box;
  padding: 16px var(--shadow-bleed) 40px;
  background: transparent;
}

.series-track {
  display: flex;
  gap: 2rem;
  align-items: stretch;
  background: transparent;
}

.product-card {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.05);
  box-shadow: 0 10px 24px -12px rgba(15, 23, 42, 0.18);
  border-radius: 16px;
  padding: 2.5rem 2rem;
  position: relative;
  overflow: visible;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  min-height: 380px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-width: 0;
  width: auto;
  max-width: 100%;
}

.product-card--carousel {
  flex: 0 0 calc((100% - (var(--visible-cards, 3) - 1) * 2rem) / var(--visible-cards, 3));
  align-self: stretch;
}

.product-card--offscreen {
  visibility: hidden;
  pointer-events: none;
}

.product-card:hover {
  transform: translateY(-8px);
  border-color: rgba(37, 99, 235, 0.3);
  box-shadow: 0 18px 32px -14px rgba(37, 99, 235, 0.28);
}

.product-card--offscreen:hover {
  transform: none;
}

.nav-btn {
  position: absolute;
  top: calc(50% + 6px);
  z-index: 3;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(37, 99, 235, 0.28);
  /* 半透明圆钮，避免大块实色底板压住卡片阴影 */
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(6px);
  color: var(--accent-color);
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 8px 20px -8px rgba(15, 23, 42, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.92);
  border-color: rgba(37, 99, 235, 0.45);
  box-shadow: 0 12px 24px -10px rgba(37, 99, 235, 0.35);
  animation: none;
  transform: translateY(-50%) scale(1.06);
}

.nav-prev {
  /* 落在内容列外侧的页面边距内，避免压卡且不收窄三卡宽度 */
  left: calc(-0.5 * var(--content-inline) - 22px);
  transform: translateY(-50%);
  animation: nav-nudge-prev 1.6s ease-in-out infinite;
}

.nav-next {
  right: calc(-0.5 * var(--content-inline) - 22px);
  transform: translateY(-50%);
  animation: nav-nudge-next 1.6s ease-in-out infinite;
}

@keyframes nav-nudge-prev {
  0%, 100% { transform: translateY(-50%) translateX(0); opacity: 1; }
  50% { transform: translateY(-50%) translateX(-4px); opacity: 0.82; }
}

@keyframes nav-nudge-next {
  0%, 100% { transform: translateY(-50%) translateX(0); opacity: 1; }
  50% { transform: translateY(-50%) translateX(4px); opacity: 0.82; }
}

@media (prefers-reduced-motion: reduce) {
  .nav-prev,
  .nav-next {
    animation: none;
  }

  .series-track {
    transition: none;
  }
}

.card-glow {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  background: radial-gradient(circle at 50% 0%, rgba(37, 99, 235, 0.08) 0%, transparent 55%);
  opacity: 0;
  transition: opacity 0.5s ease;
  pointer-events: none;
}

.product-card:hover .card-glow {
  opacity: 1;
}

.card-content {
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(6, 182, 212, 0.05) 100%);
  border: 1px solid rgba(37, 99, 235, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  overflow: hidden;
  flex-shrink: 0;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 5px;
  box-sizing: border-box;
  border-radius: 50%;
}

.default-icon {
  display: inline-block;
  width: 28px;
  height: 28px;
  background-color: var(--accent-color);
  mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>');
  -webkit-mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>');
  mask-size: cover;
  -webkit-mask-size: cover;
}

.product-name {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.product-subtitle {
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 500;
  margin-bottom: 1.2rem;
  opacity: 0.8;
  letter-spacing: 0.02em;
}

.product-desc {
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  flex: 1;
}

.product-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
}

.feature-tag {
  background: rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.1);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  color: var(--accent-color);
  font-weight: 500;
}

.learn-more {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--accent-color);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  margin-top: auto;
  transition: gap 0.2s ease;
}

.learn-more:hover {
  gap: 0.8rem;
}

.skeleton-row .skeleton-title {
  height: 24px;
  width: 180px;
  margin-bottom: 0.5rem;
}

.skeleton-row .skeleton-subtitle {
  height: 16px;
  width: 240px;
}

.skeleton {
  pointer-events: none;
}

.skeleton-icon,
.skeleton-title,
.skeleton-subtitle,
.skeleton-text {
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
  border-radius: 8px;
}

.skeleton-icon {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  margin-bottom: 1.5rem;
}

.skeleton-title {
  height: 28px;
  width: 60%;
  margin-bottom: 0.6rem;
}

.skeleton-subtitle {
  height: 18px;
  width: 40%;
  margin-bottom: 1.2rem;
}

.skeleton-text {
  height: 16px;
  width: 100%;
  margin-bottom: 0.8rem;
}

.skeleton-text.short {
  width: 80%;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.api-note {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}

.note-box {
  background: rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.15);
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--accent-color);
  box-shadow: 0 0 10px rgba(37, 99, 235, 0.4);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4); }
  70% { box-shadow: 0 0 0 6px rgba(37, 99, 235, 0); }
  100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
}

@media (max-width: 768px) {
  .series-row {
    --content-inline: 0.5rem;
    margin-bottom: 2rem;
  }

  .series-grid {
    padding: 16px var(--content-inline) 40px;
  }

  .series-carousel {
    padding: 0 var(--content-inline);
  }

  .product-card {
    min-height: auto;
    padding: 2rem 1.5rem;
  }

  .nav-prev {
    left: 0;
  }

  .nav-next {
    right: 0;
  }

  .section-desc {
    font-size: 1rem;
    margin-top: 0;
    margin-bottom: 2.5rem;
  }
}
</style>
